import { useState, useEffect, useRef } from "react";
import apiFetch from "../../apiFetch.js";
import { T, REL } from "../../theme.js";
import { usePeople } from "../../context/PeopleContext.jsx";
import { deriveIni, resizeFileToDataUrl } from "../../utils/helpers.js";
import { supabase } from "../../supabaseClient.js";
import { Capacitor } from "@capacitor/core";

export function AddPersonSheet({ onClose, onAdd, prefill }) {
  const { setNotifs } = usePeople();
  const [step, setStep] = useState(1);         // 1=identity, 2=details, 3=done
  const [showContacts, setShowContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [fullName, setFullName] = useState(prefill?.display_name || "");
  const [nickname, setNickname] = useState("");
  const [type, setType] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [anniversary, setAnniversary] = useState("");
  const [anniversaryTouched, setAnniversaryTouched] = useState(false);
  const [birthday, setBirthday] = useState("");
  const [photo, setPhoto] = useState(prefill?.photo || null);
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    resizeFileToDataUrl(file).then(url => { if (url) setPhoto(url); });
  };
  const [linkedUserId, setLinkedUserId] = useState(prefill?.id || null);
  const [nightTarget, setNightTarget] = useState(4);
  const [tgtMode, setTgtMode] = useState("month"); // "month" | "week"
  const [inviteMethod, setInviteMethod] = useState(null); // null | "email" | "qr" | "skip"
  const [inviteContact, setInviteContact] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSearchOpen, setInviteSearchOpen] = useState(false);
  const [inviteSearch, setInviteSearch] = useState("");
  const [invited, setInvited] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [orbitSearchPerformed, setOrbitSearchPerformed] = useState(false);
  const [orbitSearchContact, setOrbitSearchContact] = useState("");
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [inviteSearchLoading, setInviteSearchLoading] = useState(false);
  const [isMobileDevice] = useState(() => /Mobi|Android|iPhone|iPad|iPod/i.test(typeof navigator !== "undefined" ? navigator.userAgent : ""));
  const [contactsApiAvailable] = useState(() =>
    Capacitor.isNativePlatform() ||
    (typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window)
  );
  const nameRef = useRef(null);
  useEffect(() => { if (step === 1 && !showContacts && nameRef.current) nameRef.current.focus(); }, [step, showContacts]);

  // Track visual viewport height so the sheet stays above the keyboard on iOS
  const [vph, setVph] = useState(() => window.visualViewport?.height ?? window.innerHeight);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setVph(vv.height);
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);

  // Debounced live search for "Join Orbits" picker
  useEffect(() => {
    if (!showContacts || contactSearch.trim().length < 2) { setSearchResults([]); setSearchLoading(false); setOrbitSearchPerformed(false); return; }
    let cancelled = false;
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || cancelled) { if (!cancelled) setSearchLoading(false); return; }
        const res = await fetch(`/api/users/search?q=${encodeURIComponent(contactSearch.trim())}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (cancelled) return;
        const json = await res.json();
        setSearchResults(json.results || []);
        setOrbitSearchPerformed(true);
      } catch (_) { } finally { if (!cancelled) setSearchLoading(false); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [contactSearch, showContacts]);

  // Debounced live search for invite picker
  useEffect(() => {
    if (!inviteSearchOpen || inviteSearch.trim().length < 2) { setInviteSearchResults([]); setInviteSearchLoading(false); return; }
    let cancelled = false;
    setInviteSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || cancelled) { if (!cancelled) setInviteSearchLoading(false); return; }
        const res = await fetch(`/api/users/search?q=${encodeURIComponent(inviteSearch.trim())}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
        if (cancelled) return;
        const json = await res.json();
        setInviteSearchResults(json.results || []);
      } catch (_) { } finally { if (!cancelled) setInviteSearchLoading(false); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [inviteSearch, inviteSearchOpen]);

  const displayName = nickname.trim() || fullName.trim();
  const ini = deriveIni(fullName || nickname);
  const cfg = type ? REL[type] : null;
  const c = cfg?.color || T.clay;

  const canStep1 = type !== null;
  const anniversaryValid = type !== "partner" || (anniversary.length > 0);
  const canAdd = anniversaryValid;

  const handlePickContact = (contact) => {
    setFullName(contact.legal_full_name || contact.display_name || contact.name || "");
    setNickname("");
    if (contact.id) {
      setLinkedUserId(contact.id);
      if (contact.birthday) setBirthday(contact.birthday);
      if (contact.photo) setPhoto(contact.photo);
    } else {
      if (contact.email) { setInviteMethod("email"); setInviteEmail(contact.email); }
      else { setInviteMethod("email"); }
    }
    setShowContacts(false);
    setOrbitSearchContact("");
  };

  const handlePickFromDeviceContacts = async () => {
    if (!contactsApiAvailable) return;
    try {
      if (Capacitor.isNativePlatform()) {
        // Dynamic import — keeps @capacitor-community/contacts out of the web bundle
        const { Contacts } = await import("@capacitor-community/contacts");
        const { contact } = await Contacts.pickContact({
          projection: {
            name: true,
            phones: true,
            emails: true,
          },
        });
        if (!contact) return;
        const name = contact.name?.display || [contact.name?.given, contact.name?.family].filter(Boolean).join(" ") || "";
        const phone = contact.phones?.[0]?.number || "";
        const email = contact.emails?.[0]?.address || "";
        if (name) setFullName(name);
        if (phone) { setInviteMethod("phone"); setInvitePhone(phone); }
        else if (email) { setInviteMethod("email"); setInviteEmail(email); }
      } else {
        // Web Contacts API (Chrome on Android, etc.)
        const contacts = await navigator.contacts.select(["name", "email", "tel"], { multiple: false });
        if (contacts && contacts.length > 0) {
          const c = contacts[0];
          const name = (c.name && c.name[0]) || "";
          const email = (c.email && c.email[0]) || "";
          const phone = (c.tel && c.tel[0]) || "";
          if (name) setFullName(name);
          if (email) { setInviteMethod("email"); setInviteEmail(email); }
          else { setInviteMethod("email"); }
        }
      }
    } catch (err) {
      if (err?.message !== "pickContact cancelled") {
        console.error("[contacts]", err?.message || err);
      }
    }
  };

  const handleAdd = () => {
    const person = {
      name: displayName,
      ini,
      type,
      avail: false,
      next: "TBD",
      lastSeenAt: new Date().toISOString(),
      seen: 0,
      tgt: type === "partner" ? nightTarget : null,
      tgtMode: type === "partner" ? tgtMode : null,
      act: type === "partner" ? 0 : null,
      tags,
      anniversary: anniversary || null,
      birthday: birthday || null,
      photo: photo || null,
      linkedUserId: linkedUserId || null,
    };
    onAdd(person);
    if (linkedUserId) {
      (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;
          const res = await apiFetch("/api/connections/request", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({
              to_user_id: linkedUserId,
              type,
              tags,
              tgt: type === "partner" ? nightTarget : undefined,
              tgt_mode: tgtMode,
              anniversary: anniversary || undefined,
            }),
          });
          if (res.ok) {
            setNotifs(prev => [{
              id: `connreqsent-${linkedUserId}-${Date.now()}`,
              ini,
              type: type || "friend",
              kind: "info",
              subject: `Connection request sent to ${displayName}.`,
              time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
              read: false,
            }, ...prev]);
          }
        } catch (_) { }
      })();
    }
    setStep(3);
  };

  const addTag = (t) => {
    const cleaned = t.trim();
    if (cleaned && !tags.includes(cleaned)) setTags(ts => [...ts, cleaned]);
    setTagInput("");
  };
  const removeTag = (t) => setTags(ts => ts.filter(x => x !== t));

  const inviteDest = inviteContact ? (inviteContact.email || inviteContact.phone)
    : inviteMethod === "email" ? inviteEmail : null;
  const inviteReady = inviteMethod === "skip" || inviteMethod === "qr" || (inviteDest && inviteDest.length > 3);

  return (
    <div onClick={onClose} style={{ position: "fixed", top: 0, left: 0, right: 0, height: vph, background: "rgba(30,16,8,0.45)", zIndex: 600, display: "flex", alignItems: "flex-end", justifyContent: "center", animation: "fadeIn .15s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, background: T.bg, borderRadius: "22px 22px 0 0", boxShadow: "0 -8px 48px rgba(30,16,8,.2)", display: "flex", flexDirection: "column", maxHeight: vph * 0.97, animation: "fadeUp .22s ease" }}>

        {/* Handle + header */}
        <div style={{ padding: "14px 22px 0", flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: T.edgeMid, margin: "0 auto 14px" }} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <h2 style={{ fontSize: 20, fontFamily: "'Crimson Pro',serif", fontWeight: 400, color: T.ink, letterSpacing: "-0.01em" }}>
              {step === 1 ? "Add someone" : step === 2 ? `About ${displayName || "them"}` : linkedUserId ? "Request sent ✓" : "Added ✓"}
            </h2>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: "50%", background: T.mist, border: `1px solid ${T.edge}`, cursor: "pointer", color: T.muted, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
          </div>
          {step < 3 && <>
            <p style={{ fontSize: 11, color: T.muted, marginBottom: 8 }}>
              Step {step} of 2 — {step === 1 ? "Basic info" : "Details"}
            </p>
            <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
              {[1, 2].map(i => <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? c : T.edge, transition: "background .25s" }} />)}
            </div>
          </>}
        </div>

        {/* ── STEP 1: Identity ── */}
        {step === 1 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 22px 8px" }}>

            {/* Import from contacts */}
            {!showContacts && !linkedUserId && (
              <button onClick={() => setShowContacts(true)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "11px 14px", borderRadius: 13, background: T.surface, border: `1.5px dashed ${T.clay}60`, cursor: "pointer", marginBottom: 18, transition: "border-color .15s", textAlign: "left" }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.clay + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>🔗</div>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.bark }}>Merge Orbits</p>
                  <p style={{ fontSize: 11, color: T.muted }}>Connect constellations with an existing Orbit user</p>
                </div>
                <span style={{ fontSize: 16, color: T.edgeMid, marginLeft: "auto" }}>›</span>
              </button>
            )}
            {/* Linked Orbit profile card */}
            {!showContacts && linkedUserId && (
              <div style={{ background: T.sage + "15", border: `1px solid ${T.sage}33`, borderRadius: 12, padding: "10px 13px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.sage + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.sage, flexShrink: 0, overflow: "hidden" }}>
                  {photo ? <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" /> : deriveIni(fullName || "?")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fullName}</p>
                  <p style={{ fontSize: 11, color: T.sage }}>Linked to Orbit account{birthday ? ` · Birthday loaded` : ""}{photo ? ` · Photo loaded` : ""}</p>
                </div>
                <button onClick={() => { setLinkedUserId(null); setFullName(""); setBirthday(""); setPhoto(null); setOrbitSearchContact(""); setShowContacts(true); }} style={{ background: "none", border: `1px solid ${T.edge}`, borderRadius: 7, padding: "4px 9px", cursor: "pointer", fontSize: 11, color: T.muted, flexShrink: 0 }}>Change</button>
              </div>
            )}

            {/* Join Orbits picker */}
            {showContacts && (
              <div style={{ marginBottom: 18, animation: "fadeUp .18s ease" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <button onClick={() => { setShowContacts(false); setOrbitSearchContact(""); setSearchResults([]); setOrbitSearchPerformed(false); }} style={{ width: 28, height: 28, borderRadius: "50%", background: T.mist, border: "none", cursor: "pointer", color: T.muted, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>←</button>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.bark }}>Find an Orbiter</p>
                </div>
                <input
                  value={contactSearch}
                  onChange={e => setContactSearch(e.target.value)}
                  placeholder="Name, email, or phone…"
                  autoFocus
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 11, border: `1.5px solid ${contactSearch.trim().length >= 2 ? T.clay + "60" : T.edge}`, background: T.surface, fontSize: 13, color: T.ink, fontFamily: "'DM Sans',sans-serif", marginBottom: 8 }}
                />
                <div style={{ background: T.surface, borderRadius: 12, border: `1px solid ${T.edge}`, overflow: "hidden" }}>
                  {searchLoading && <p style={{ padding: "14px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>Searching…</p>}
                  {!searchLoading && !orbitSearchPerformed && <p style={{ padding: "14px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>Start typing to find an Orbiter</p>}
                  {!searchLoading && orbitSearchPerformed && searchResults.length === 0 && <p style={{ padding: "14px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>No Orbiters found — try a different name, email, or phone</p>}
                  {!searchLoading && searchResults.map((ct, i) => (
                    <button key={ct.id} onClick={() => handlePickContact(ct)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "11px 14px", border: "none", borderBottom: i < searchResults.length - 1 ? `1px solid ${T.edge}` : "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.mist, border: `1px solid ${T.edge}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.mid, flexShrink: 0, overflow: "hidden" }}>
                        {ct.photo ? <img src={ct.photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" /> : deriveIni(ct.display_name || "?")}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{ct.display_name}</p>
                        <p style={{ fontSize: 11, color: T.muted }}>{ct.email || ct.phone}</p>
                      </div>
                      <span style={{ fontSize:11, color: T.sage, fontWeight: 600, flexShrink: 0 }}>On Orbit ✓</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setShowContacts(false); setOrbitSearchContact(""); setSearchResults([]); setOrbitSearchPerformed(false); }} style={{ marginTop: 10, background: "none", border: "none", fontSize: 12, color: T.muted, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 2 }}>Expand Your Orbit manually instead</button>
              </div>
            )}

            {/* Name fields */}
            {!showContacts && (<>
              {!linkedUserId && <>
                <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: contactsApiAvailable ? 10 : 14 }}>
                  <div style={{ flex: 1, height: 1, background: T.edge }} />
                  <span style={{ fontSize:11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0 }}>Expand Your Orbit</span>
                  <div style={{ flex: 1, height: 1, background: T.edge }} />
                </div>
                {Capacitor.isNativePlatform() && contactsApiAvailable && (
                  <button onClick={handlePickFromDeviceContacts} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 13px", borderRadius: 11, background: T.surface, border: `1px solid ${T.edge}`, cursor: "pointer", marginBottom: 12, textAlign: "left" }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>📱</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 12, fontWeight: 500, color: T.bark }}>Import from your contacts</p>
                      <p style={{ fontSize:11, color: T.muted }}>Pick a contact to pre-fill this form</p>
                    </div>
                    <span style={{ fontSize: 14, color: T.edgeMid }}>›</span>
                  </button>
                )}
              </>}

              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 18 }}>
                <div>
                  <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.07em" }}>Full name</label>
                  <input
                    ref={nameRef}
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && canStep1 && setStep(2)}
                    placeholder="e.g. Alex Kim"
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${fullName.trim() ? c + "60" : T.edge}`, background: T.surface, fontSize: 15, color: T.ink, fontFamily: "'DM Sans',sans-serif", transition: "border-color .15s" }}
                  />
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>Used for invites and formal references</p>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.07em" }}>Nickname / display name <span style={{ color: T.edgeMid, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    value={nickname}
                    onChange={e => setNickname(e.target.value)}
                    placeholder={fullName.trim() ? `e.g. ${fullName.trim().split(" ")[0]}, Lex, Babe…` : "What you call them"}
                    style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${nickname.trim() ? c + "60" : T.edge}`, background: T.surface, fontSize: 15, color: T.ink, fontFamily: "'DM Sans',sans-serif", transition: "border-color .15s" }}
                  />
                  <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>How they'll appear in the app</p>
                </div>
              </div>

              {/* Live preview */}
              {fullName.trim() && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 11, background: T.surface, border: `1px solid ${T.edge}`, marginBottom: 18, animation: "fadeUp .15s ease" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: c + "20", border: `1.5px solid ${c}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: c, fontFamily: "'Crimson Pro',serif", flexShrink: 0 }}>{ini}</div>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{displayName}</p>
                    {nickname.trim() && <p style={{ fontSize: 11, color: T.muted }}>Full name: {fullName.trim()}</p>}
                  </div>
                </div>
              )}
            </>)}

            {/* Relationship type */}
            {!showContacts && (<>
              <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 9, textTransform: "uppercase", letterSpacing: "0.07em" }}>Relationship type <span style={{ color: T.red, fontWeight: 400 }}>*</span></label>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 24 }}>
                {Object.entries(REL).map(([key, rcfg]) => (
                  <button key={key} onClick={() => setType(key)} style={{ display: "flex", alignItems: "center", gap: 13, padding: "12px 15px", borderRadius: 13, cursor: "pointer", background: type === key ? rcfg.color + "12" : T.surface, border: `1.5px solid ${type === key ? rcfg.color : T.edge}`, transition: "all .14s", textAlign: "left" }}>
                    <span style={{ fontSize: 20, color: rcfg.color, flexShrink: 0 }}>{rcfg.icon}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: type === key ? T.ink : T.bark }}>{rcfg.label}</p>
                      <p style={{ fontSize: 11, color: T.muted }}>{rcfg.plural}</p>
                    </div>
                    {type === key && <div style={{ width: 18, height: 18, borderRadius: "50%", background: rcfg.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span style={{ fontSize:11, color: "white", fontWeight: 700, lineHeight: 1 }}>✓</span></div>}
                  </button>
                ))}
              </div>
            </>)}
          </div>
        )}

        {/* ── STEP 2: Details ── */}
        {step === 2 && (
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 22px 8px" }}>

            {/* Identity preview pill */}
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 13px", borderRadius: 13, background: T.surface, border: `1px solid ${T.edge}`, marginBottom: 20 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: c + "20", border: `1.5px solid ${c}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: c, fontFamily: "'Crimson Pro',serif", flexShrink: 0 }}>{ini}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{displayName}</p>
                {nickname.trim() && <p style={{ fontSize: 11, color: T.muted }}>Full name: {fullName.trim()}</p>}
              </div>
              <span style={{ fontSize:11, color: c, background: c + "14", padding: "2px 7px", borderRadius: 6, fontWeight: 600, flexShrink: 0 }}>{cfg?.icon} {cfg?.label}</span>
            </div>

            {/* Photo upload */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 20 }}>
              <input id="add-person-photo" type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: "none" }} />
              <label htmlFor="add-person-photo" style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: photo ? "transparent" : c + "18", border: `2px dashed ${photo ? c : T.edge}`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "all .15s" }}>
                  {photo ? <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 28 }}>📷</span>}
                </div>
                <span style={{ fontSize: 11, color: c, fontWeight: 600 }}>{photo ? "Change photo" : "Add photo"} <span style={{ color: T.muted, fontWeight: 400 }}>(optional)</span></span>
              </label>
              {photo && <button onClick={() => setPhoto(null)} style={{ marginTop: 4, fontSize:11, color: T.muted, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>Remove</button>}
            </div>

            {/* Birthday */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Birthday <span style={{ color: T.edgeMid, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
              </label>
              <input
                type="date"
                value={birthday}
                onChange={e => setBirthday(e.target.value)}
                style={{ width: "100%", padding: "11px 13px", borderRadius: 12, border: `1.5px solid ${birthday ? c + "60" : T.edge}`, background: T.surface, fontSize: 14, color: T.ink, fontFamily: "'DM Sans',sans-serif", cursor: "pointer", transition: "border-color .15s" }}
              />
              {birthday && (
                <p style={{ fontSize: 11, color: T.sage, marginTop: 5 }}>✓ {new Date(birthday + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric" })}</p>
              )}
            </div>

            {/* Anniversary — required for partner, optional for coparent */}
            {(type === "partner" || type === "coparent") && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                  {type === "partner" ? "Anniversary" : "Parenting start date"}
                  {type === "partner"
                    ? <span style={{ color: T.red, fontWeight: 400 }}> *</span>
                    : <span style={{ color: T.edgeMid, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}> (optional)</span>}
                </label>
                <input
                  type="date"
                  value={anniversary}
                  onChange={e => { setAnniversary(e.target.value); setAnniversaryTouched(true); }}
                  onBlur={() => setAnniversaryTouched(true)}
                  style={{ width: "100%", padding: "11px 13px", borderRadius: 12, border: `1.5px solid ${anniversaryTouched && type === "partner" && !anniversary ? T.red : anniversary ? c + "60" : T.edge}`, background: T.surface, fontSize: 14, color: T.ink, fontFamily: "'DM Sans',sans-serif", cursor: "pointer", transition: "border-color .15s" }}
                />
                {anniversaryTouched && type === "partner" && !anniversary && (
                  <p style={{ fontSize: 11, color: T.red, marginTop: 5, animation: "fadeUp .15s ease" }}>Anniversary is required for partners — it powers fairness tracking and reminders.</p>
                )}
                {anniversary && (
                  <p style={{ fontSize: 11, color: T.sage, marginTop: 5 }}>✓ {new Date(anniversary + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                )}
              </div>
            )}

            {/* Night target — partners only */}
            {type === "partner" && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Night target <span style={{ color: T.edgeMid, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(fairness tracking)</span></label>
                <div style={{ display: "flex", gap: 0, marginBottom: 10, background: T.mist, borderRadius: 10, padding: 3, border: `1px solid ${T.edge}`, alignSelf: "flex-start", width: "fit-content" }}>
                  {[{ id: "week", label: "Per week" }, { id: "month", label: "Per month" }].map(m => (
                    <button key={m.id} onClick={() => {
                      if (m.id === tgtMode) return;
                      if (m.id === "month") setNightTarget(n => Math.round(Math.min(30, n * 4.33)));
                      else setNightTarget(n => Math.max(1, Math.round(n / 4.33)));
                      setTgtMode(m.id);
                    }} style={{ padding: "5px 16px", borderRadius: 8, border: "none", cursor: "pointer", background: tgtMode === m.id ? c : T.mist, color: tgtMode === m.id ? "white" : T.muted, fontSize: 11, fontWeight: tgtMode === m.id ? 600 : 400, transition: "all .12s" }}>{m.label}</button>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", borderRadius: 12, background: T.surface, border: `1px solid ${T.edge}` }}>
                  <button onClick={() => setNightTarget(n => Math.max(1, n - 1))} style={{ width: 32, height: 32, borderRadius: 9, background: T.mist, border: `1px solid ${T.edge}`, cursor: "pointer", fontSize: 17, color: T.bark, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>−</button>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <span style={{ fontSize: 26, fontWeight: 700, color: c, fontFamily: "'Crimson Pro',serif" }}>{nightTarget}</span>
                    <span style={{ fontSize: 12, color: T.muted, marginLeft: 5 }}>nights / {tgtMode === "week" ? "week" : "month"}</span>
                  </div>
                  <button onClick={() => setNightTarget(n => Math.min(tgtMode === "week" ? 7 : 30, n + 1))} style={{ width: 32, height: 32, borderRadius: 9, background: T.mist, border: `1px solid ${T.edge}`, cursor: "pointer", fontSize: 17, color: T.bark, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>+</button>
                </div>
                <div style={{ display: "flex", gap: 5, marginTop: 8, justifyContent: "center" }}>
                  {(tgtMode === "week" ? [1, 2, 3, 4, 5] : [2, 4, 6, 8, 12]).map(n => (
                    <button key={n} onClick={() => setNightTarget(n)} style={{ padding: "3px 11px", borderRadius: 20, background: nightTarget === n ? c + "18" : T.mist, border: `1px solid ${nightTarget === n ? c + "50" : T.edge}`, color: nightTarget === n ? c : T.muted, fontSize: 11, cursor: "pointer", fontWeight: nightTarget === n ? 700 : 400, transition: "all .12s" }}>{n}</button>
                  ))}
                </div>
                {tgtMode === "week" && <p style={{ fontSize:11, color: T.muted, textAlign: "center", marginTop: 6 }}>≈ {Math.round(nightTarget * 4.33)} nights / month</p>}
                {tgtMode === "month" && nightTarget >= 4 && <p style={{ fontSize:11, color: T.muted, textAlign: "center", marginTop: 6 }}>≈ {(nightTarget / 4.33).toFixed(1)} nights / week</p>}
              </div>
            )}

            {/* Tags */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>Tags <span style={{ color: T.edgeMid, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              {type && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 7 }}>
                  {({
                    partner: ["nesting", "long distance", "anchor nights", "no overnights", "local"],
                    family: ["local", "sibling", "parent", "weekly visit", "school pickup"],
                    friend: ["weekly", "monthly", "old friend", "work friend", "neighborhood"],
                    coparent: ["custody split", "50/50", "OFW", "same school", "local"],
                  }[type] || []).filter(s => !tags.includes(s)).slice(0, 4).map(s => (
                    <button key={s} onClick={() => addTag(s)} style={{ padding: "3px 10px", borderRadius: 20, background: T.mist, border: `1px solid ${T.edge}`, color: T.muted, fontSize: 11, cursor: "pointer" }}>+ {s}</button>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: tags.length ? 7 : 0 }}>
                {tags.map(t => (
                  <div key={t} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 9px 3px 10px", borderRadius: 20, background: c + "14", border: `1px solid ${c}30`, color: c, fontSize: 11, fontWeight: 500 }}>
                    {t}<button onClick={() => removeTag(t)} style={{ background: "none", border: "none", cursor: "pointer", color: c, fontSize: 11, lineHeight: 1, padding: 0, marginLeft: 2 }}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 7, marginTop: 7 }}>
                <input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); } }}
                  placeholder="Custom tag…"
                  style={{ flex: 1, padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${T.edge}`, background: T.surface, fontSize: 13, color: T.ink, fontFamily: "'DM Sans',sans-serif" }}
                />
                <button onClick={() => addTag(tagInput)} disabled={!tagInput.trim()} style={{ padding: "9px 14px", borderRadius: 10, background: tagInput.trim() ? c : T.edgeMid, border: "none", color: "white", fontSize: 12, fontWeight: 600, cursor: tagInput.trim() ? "pointer" : "default", transition: "background .15s" }}>Add</button>
              </div>
            </div>

            {/* Invite */}
            {!linkedUserId && <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, color: T.muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.07em" }}>Send invite to orbit? <span style={{ color: T.edgeMid, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              {inviteMethod === null && (
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  <button onClick={() => setInviteSearchOpen(true)} style={{ display: "flex", alignItems: "center", gap: 11, padding: "11px 14px", borderRadius: 12, background: T.surface, border: `1.5px solid ${T.edge}`, cursor: "pointer", textAlign: "left", transition: "border-color .15s" }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>👤</span>
                    <div style={{ flex: 1 }}><p style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>Pick from contacts</p><p style={{ fontSize: 11, color: T.muted }}>Finds their phone or email automatically</p></div>
                    <span style={{ fontSize: 14, color: T.edgeMid }}>›</span>
                  </button>
                  <div style={{ display: "flex", gap: 7 }}>
                    <button onClick={() => setInviteMethod("email")} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "10px 8px", borderRadius: 12, background: T.surface, border: `1.5px solid ${T.edge}`, cursor: "pointer", fontSize: 11, color: T.bark, fontWeight: 500 }}>
                      <span style={{ fontSize: 18 }}>✉️</span>Email
                    </button>
                    <button onClick={() => setInviteMethod("qr")} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "10px 8px", borderRadius: 12, background: T.surface, border: `1.5px solid ${T.edge}`, cursor: "pointer", fontSize: 11, color: T.bark, fontWeight: 500 }}>
                      <span style={{ fontSize: 18 }}>▦</span>QR code
                    </button>
                    <button onClick={() => setInviteMethod("skip")} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, padding: "10px 8px", borderRadius: 12, background: T.surface, border: `1.5px solid ${T.edge}`, cursor: "pointer", fontSize: 11, color: T.muted }}>
                      <span style={{ fontSize: 18, opacity: .4 }}>○</span>Skip
                    </button>
                  </div>
                </div>
              )}

              {/* Contacts search for invite */}
              {inviteSearchOpen && (
                <div style={{ animation: "fadeUp .15s ease" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <button onClick={() => setInviteSearchOpen(false)} style={{ width: 26, height: 26, borderRadius: "50%", background: T.mist, border: "none", cursor: "pointer", color: T.muted, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>←</button>
                    <input value={inviteSearch} onChange={e => setInviteSearch(e.target.value)} placeholder="Search contacts…" autoFocus style={{ flex: 1, padding: "8px 11px", borderRadius: 10, border: `1.5px solid ${T.edge}`, background: T.surface, fontSize: 13, color: T.ink, fontFamily: "'DM Sans',sans-serif" }} />
                  </div>
                  <div style={{ background: T.surface, borderRadius: 11, border: `1px solid ${T.edge}`, overflow: "hidden" }}>
                    {inviteSearchLoading && <p style={{ padding: "12px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>Searching…</p>}
                    {!inviteSearchLoading && inviteSearch.trim().length < 2 && <p style={{ padding: "12px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>Type to search Orbit users…</p>}
                    {!inviteSearchLoading && inviteSearch.trim().length >= 2 && inviteSearchResults.length === 0 && <p style={{ padding: "12px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>No users found</p>}
                    {!inviteSearchLoading && inviteSearchResults.map((ct, i) => (
                      <button key={ct.id} onClick={() => { setInviteContact({ id: ct.id, name: ct.display_name || "", ini: deriveIni(ct.display_name || "?"), phone: ct.phone || null, email: ct.email || null }); setInviteMethod("contact"); setLinkedUserId(ct.id); setInviteSearchOpen(false); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", border: "none", borderBottom: i < inviteSearchResults.length - 1 ? `1px solid ${T.edge}` : "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.mist, border: `1px solid ${T.edge}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: T.mid, flexShrink: 0 }}>{deriveIni(ct.display_name || "?")}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{ct.display_name}</p>
                          <p style={{ fontSize: 11, color: T.muted }}>{ct.email || ct.phone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}


              {/* Email input */}
              {inviteMethod === "email" && !inviteSearchOpen && (
                <div style={{ animation: "fadeUp .15s ease" }}>
                  <div style={{ display: "flex", gap: 7 }}>
                    <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="their@email.com" type="email" autoFocus style={{ flex: 1, padding: "11px 13px", borderRadius: 11, border: `1.5px solid ${inviteEmail.includes("@") ? c + "60" : T.edge}`, background: T.surface, fontSize: 14, color: T.ink, fontFamily: "'DM Sans',sans-serif" }} />
                    <button onClick={() => { setInviteMethod(null); setInviteEmail(""); }} style={{ width: 36, height: 44, borderRadius: 11, background: T.mist, border: `1px solid ${T.edge}`, cursor: "pointer", color: T.muted, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
                  </div>
                </div>
              )}

              {/* Contact picked */}
              {inviteMethod === "contact" && inviteContact && !inviteSearchOpen && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 13px", borderRadius: 12, background: c + "08", border: `1.5px solid ${c}30`, animation: "fadeUp .15s ease" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: c + "20", border: `1.5px solid ${c}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: c, flexShrink: 0 }}>{inviteContact.ini}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{inviteContact.name}</p>
                    <p style={{ fontSize: 11, color: T.muted }}>{inviteContact.phone || inviteContact.email}</p>
                  </div>
                  <button onClick={() => { setInviteMethod(null); setInviteContact(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, fontSize: 12 }}>✕</button>
                </div>
              )}

              {/* Skip confirmation */}
              {inviteMethod === "skip" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 11, background: T.mist, border: `1px solid ${T.edge}`, animation: "fadeUp .15s ease" }}>
                  <span style={{ fontSize: 12, color: T.muted, flex: 1 }}>No invite — you can send one later from their profile.</span>
                  <button onClick={() => setInviteMethod(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, fontSize: 12, flexShrink: 0 }}>Change</button>
                </div>
              )}

              {/* QR code display */}
              {inviteMethod === "qr" && (
                <div style={{ animation: "fadeUp .18s ease" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "18px 16px 14px", borderRadius: 16, background: T.surface, border: `1.5px solid ${c}30`, position: "relative" }}>
                    <p style={{ fontSize: 12, color: T.muted, marginBottom: 14, textAlign: "center" }}>Show this to {displayName || "them"} — they scan it to join orbit</p>
                    <div style={{ position: "relative", width: 160, height: 160, borderRadius: 12, background: T.bark, padding: 10, marginBottom: 14, boxShadow: `0 4px 20px ${T.bark}30` }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 2, width: "100%", height: "100%" }}>
                        {Array.from({ length: 100 }).map((_, i) => {
                          const row = Math.floor(i / 10), col = i % 10;
                          const inTL = (row < 3 && col < 3) || (row === 3 && col < 3) || (row < 3 && col === 3);
                          const inTR = (row < 3 && col > 6) || (row === 3 && col > 6) || (row < 3 && col === 6);
                          const inBL = (row > 6 && col < 3) || (row === 6 && col < 3) || (row > 6 && col === 3);
                          const finder = inTL || inTR || inBL;
                          const seed = (i * 17 + 13) % 31;
                          const on = finder || (seed > 12 && !((row >= 1 && row <= 2) && (col >= 1 && col <= 2)) && !((row >= 1 && row <= 2) && (col >= 8)) && !((row >= 8) && (col >= 1 && col <= 2)));
                          return (<div key={i} style={{ borderRadius: 1, background: on ? "white" : "transparent" }} />);
                        })}
                      </div>
                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg,${T.partner},${T.coparent})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, animation: "pulse 2s ease infinite", border: "2px solid white" }}>◎</div>
                    </div>
                    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 7, padding: "8px 10px", borderRadius: 10, background: T.mist, border: `1px solid ${T.edge}`, marginBottom: 10 }}>
                      <span style={{ fontSize: 11, color: T.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>orbit.app/join/morgan-{(displayName || "them").toLowerCase().replace(/\s+/g, "-")}</span>
                      <button onClick={() => setInvited(true)} style={{ padding: "4px 10px", borderRadius: 7, background: invited ? T.sage : c, border: "none", color: "white", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0, transition: "background .2s" }}>{invited ? "Copied ✓" : "Copy"}</button>
                    </div>
                    <p style={{ fontSize:11, color: T.muted, textAlign: "center", lineHeight: 1.5 }}>Link expires in 7 days · Works for any device</p>
                    <button onClick={() => { setInviteMethod(null); setInvited(false); }} style={{ position: "absolute", top: 10, right: 12, background: "none", border: "none", cursor: "pointer", color: T.muted, fontSize: 13 }}>✕</button>
                  </div>
                </div>
              )}
            </div>}

          </div>
        )}

        {/* ── STEP 3: Done ── */}
        {step === 3 && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "8px 24px 12px", animation: "popIn .3s ease" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: c + "18", border: `2px solid ${c}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 700, color: c, fontFamily: "'Crimson Pro',serif", marginBottom: 14, boxShadow: `0 4px 20px ${c}25`, overflow: "hidden" }}>
              {photo ? <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" /> : ini}
            </div>
            {linkedUserId ? (
              <>
                <h3 style={{ fontSize: 22, fontFamily: "'Crimson Pro',serif", fontWeight: 400, color: T.ink, marginBottom: 4 }}>{displayName} added</h3>
                <p style={{ fontSize: 13, color: T.muted, marginBottom: 12, textAlign: "center", lineHeight: 1.6 }}>
                  {cfg?.icon} {cfg?.label}{tags.length > 0 ? ` · ${tags.join(", ")}` : ""}{nickname.trim() ? <><br /><span style={{ fontSize: 11 }}>Full name: {fullName.trim()}</span></> : ""}
                </p>
                <div style={{ width: "100%", padding: "13px 16px", borderRadius: 13, background: T.clay + "10", border: `1px solid ${T.clay}30`, marginBottom: 16 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.clay, marginBottom: 4 }}>🔗 Connection request sent</p>
                  <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>{displayName} will see your request and can accept or decline. Once they accept, you'll both appear in each other's orbits.</p>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ fontSize: 22, fontFamily: "'Crimson Pro',serif", fontWeight: 400, color: T.ink, marginBottom: 4 }}>{displayName} added</h3>
                <p style={{ fontSize: 13, color: T.muted, marginBottom: 20, textAlign: "center", lineHeight: 1.6 }}>
                  {cfg?.icon} {cfg?.label}{tags.length > 0 ? ` · ${tags.join(", ")}` : ""}{nickname.trim() ? <><br /><span style={{ fontSize: 11 }}>Full name: {fullName.trim()}</span></> : ""}
                </p>
              </>
            )}
            {/* Invite status */}
            {inviteMethod === "qr" && (
              <div style={{ width: "100%", background: T.surface, borderRadius: 14, border: `1px solid ${T.edge}`, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ padding: "11px 16px", borderBottom: `1px solid ${T.edge}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <p style={{ fontSize: 12, color: T.muted }}>QR invite ready</p>
                  {invited && <span style={{ fontSize: 11, color: T.sage, fontWeight: 600 }}>✓ Link copied</span>}
                </div>
                <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <div style={{ position: "relative", width: 130, height: 130, borderRadius: 10, background: T.bark, padding: 8, boxShadow: `0 4px 16px ${T.bark}25` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 1.5, width: "100%", height: "100%" }}>
                      {Array.from({ length: 100 }).map((_, i) => {
                        const row = Math.floor(i / 10), col = i % 10;
                        const inTL = (row < 3 && col < 3) || (row === 3 && col < 3) || (row < 3 && col === 3);
                        const inTR = (row < 3 && col > 6) || (row === 3 && col > 6) || (row < 3 && col === 6);
                        const inBL = (row > 6 && col < 3) || (row === 6 && col < 3) || (row > 6 && col === 3);
                        const finder = inTL || inTR || inBL;
                        const seed = (i * 17 + 13) % 31;
                        const on = finder || (seed > 12 && !((row >= 1 && row <= 2) && (col >= 1 && col <= 2)) && !((row >= 1 && row <= 2) && (col >= 8)) && !((row >= 8) && (col >= 1 && col <= 2)));
                        return (<div key={i} style={{ borderRadius: 1, background: on ? "white" : "transparent" }} />);
                      })}
                    </div>
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 24, height: 24, borderRadius: "50%", background: `linear-gradient(135deg,${T.partner},${T.coparent})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize:11, animation: "pulse 2s ease infinite", border: "2px solid white" }}>◎</div>
                  </div>
                  <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 7, padding: "7px 10px", borderRadius: 10, background: T.mist, border: `1px solid ${T.edge}` }}>
                    <span style={{ fontSize: 11, color: T.muted, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>orbit.app/join/morgan-{(displayName || "them").toLowerCase().replace(/\s+/g, "-")}</span>
                    <button onClick={() => setInvited(true)} style={{ padding: "4px 10px", borderRadius: 7, background: invited ? T.sage : c, border: "none", color: "white", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0, transition: "background .2s" }}>{invited ? "Copied ✓" : "Copy"}</button>
                  </div>
                </div>
              </div>
            )}
            {inviteMethod && inviteMethod !== "skip" && inviteMethod !== "qr" && inviteDest && !linkedUserId && (
              <div style={{ width: "100%", background: T.surface, borderRadius: 14, border: `1px solid ${T.edge}`, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ padding: "11px 16px", borderBottom: `1px solid ${T.edge}` }}>
                  <p style={{ fontSize: 12, color: T.muted }}>Invite to orbit</p>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <p style={{ fontSize: 12, color: T.bark, marginBottom: 11, lineHeight: 1.6 }}>
                    Send {displayName} an invite so they can connect and share their availability with you.
                  </p>
                  {!invited ? (
                    <button onClick={() => setInvited(true)} style={{ width: "100%", padding: "10px", borderRadius: 11, background: c, border: "none", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: `0 3px 12px ${c}35` }}>
                      Send to {inviteDest} ↗
                    </button>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: T.sage + "10", border: `1px solid ${T.sage}30` }}>
                      <span style={{ fontSize: 13, color: T.sage }}>✓</span>
                      <div><p style={{ fontSize: 12, color: T.sage, fontWeight: 600 }}>Invite sent</p><p style={{ fontSize: 11, color: T.muted }}>to {inviteDest}</p></div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!linkedUserId && (inviteMethod === "skip" || !inviteMethod) && (
              <div style={{ width: "100%", padding: "11px 14px", borderRadius: 12, background: T.surface, border: `1px solid ${T.edge}`, marginBottom: 16 }}>
                <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>You can invite {displayName} to orbit anytime from their profile — it only takes a moment.</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "10px 22px 30px", flexShrink: 0, borderTop: `1px solid ${T.edge}` }}>
          {step === 1 && (
            <button onClick={() => canStep1 && !showContacts && setStep(2)} style={{ width: "100%", padding: "14px", borderRadius: 14, background: canStep1 && !showContacts ? T.bark : T.edgeMid, border: "none", color: "white", fontSize: 15, fontWeight: 600, cursor: canStep1 && !showContacts ? "pointer" : "default", transition: "background .15s", boxShadow: canStep1 && !showContacts ? `0 4px 18px ${T.bark}33` : "none" }}>
              Continue →
            </button>
          )}
          {step === 2 && (
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: "14px", borderRadius: 14, background: T.mist, border: `1px solid ${T.edge}`, color: T.bark, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>← Back</button>
              <button
                onClick={() => { setAnniversaryTouched(true); canAdd && handleAdd(); }}
                style={{ flex: 2, padding: "14px", borderRadius: 14, background: canAdd ? `linear-gradient(135deg,${T.bark},${c})` : T.edgeMid, border: "none", color: "white", fontSize: 14, fontWeight: 600, cursor: canAdd ? "pointer" : "default", transition: "background .15s", boxShadow: canAdd ? `0 4px 18px ${T.bark}28` : "none" }}
              >Add {displayName || "person"} →</button>
            </div>
          )}
          {step === 3 && (
            <button onClick={onClose} style={{ width: "100%", padding: "14px", borderRadius: 14, background: T.bark, border: "none", color: T.parchment, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Done</button>
          )}
        </div>
      </div>
    </div>
  );
}
